import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const MidenExecutionContext = createContext();

export const useMidenExecution = () => {
  const context = useContext(MidenExecutionContext);
  if (!context) {
    throw new Error('useMidenExecution must be used within a MidenExecutionProvider');
  }
  return context;
};

export const MidenExecutionProvider = ({ children }) => {
  const [webClient, setWebClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize Miden Web Client
  const initializeClient = useCallback(async () => {
    if (webClient) return webClient;
    
    setLoading(true);
    setError(null);

    try {
      // Dynamic import of Miden SDK
      const { WebClient } = await import('@demox-labs/miden-sdk');
      
      // Create client for testnet
      const client = await WebClient.createClient();
      setWebClient(client);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Miden Web Client initialized successfully');
      }
      return client;
    } catch (err) {
      console.error('Failed to initialize Miden client:', err);
      setError('Failed to initialize Miden execution environment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [webClient]);

  // Execute Miden assembly code
  const executeCode = useCallback(async (code, inputs = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Run in Web Worker with timeout
      const worker = new Worker(new URL('../workers/midenWorker.js', import.meta.url));
      const result = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          try { worker.terminate(); } catch (_) {}
          reject(new Error('Execution timeout (5s)')); 
        }, 5000);
        worker.onmessage = (evt) => {
          clearTimeout(timeout);
          try { worker.terminate(); } catch (_) {}
          const { ok, data, error } = evt.data || {};
          if (!ok) return reject(new Error(error || 'Execution failed'));
          resolve(data);
        };
        worker.onerror = (e) => {
          clearTimeout(timeout);
          try { worker.terminate(); } catch (_) {}
          reject(new Error(e.message || 'Worker error'));
        };
        worker.postMessage({ code, inputs });
      });
      return result;
    } catch (err) {
      console.error('Code execution failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // NOTE: legacy inline interpreter removed; execution now handled in Web Worker

  // Create a new account (placeholder)
  const createAccount = useCallback(async (storageMode = 'public') => {
    if (!webClient) {
      await initializeClient();
    }

    try {
      // This would use the actual Miden SDK account creation
      const accountId = `acc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return {
        id: accountId,
        storageMode,
        balance: 0,
        created: new Date().toISOString()
      };
    } catch (err) {
      console.error('Account creation failed:', err);
      throw err;
    }
  }, [webClient, initializeClient]);

  // Get account balance (placeholder)
  const getAccountBalance = useCallback(async (accountId) => {
    if (!webClient) {
      await initializeClient();
    }

    try {
      // This would query the actual Miden network
      return Math.floor(Math.random() * 1000); // Mock balance
    } catch (err) {
      console.error('Balance query failed:', err);
      throw err;
    }
  }, [webClient, initializeClient]);

  // Initialize client on mount
  useEffect(() => {
    // Initialize client best-effort; worker path does not depend on it
    initializeClient().catch(() => {});
  }, [initializeClient]);

  const value = {
    webClient,
    loading,
    error,
    executeCode,
    createAccount,
    getAccountBalance,
    initializeClient
  };

  return (
    <MidenExecutionContext.Provider value={value}>
      {children}
    </MidenExecutionContext.Provider>
  );
};
